import {
  makeStyles,
  Typography,
  Tooltip,
  Link as MaterialLink,
  IconButton,
} from '@material-ui/core';
import AccountBox from '@material-ui/icons/AccountBox';
import AssignmentIcon from '@material-ui/icons/Assignment';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import BallotIcon from '@material-ui/icons/Ballot';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ListAltIcon from '@material-ui/icons/ListAlt';
import cn from 'classnames';
import { format } from 'date-fns';
import { throttle } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ACCEPTANCE_STATUS, Event as EventType } from '../../api/events';
import { CopyIcon } from '../../assets/icons';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { getAttendanceStatusColor } from '../../utils/attendance';
import { Text, Link } from '../common';
import SessionsRowActions from '../sessions/sessions-row-actions';

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  date: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    margin: 0,
    fontSize: 16,
    paddingBottom: 10,
  },
  cardTitle: {
    marginBottom: 4,
    fontSize: 18,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    '& > a': {
      color: COLORS.COLOR_TEXT_BASE,
      textDecoration: 'none',
    },
  },
  icon: {
    color: COLORS.COLOR_GRAY_BASE,
    cursor: 'pointer',

    '&:hover': {
      color: COLORS.COLOR_BLUE_BASE,
    },
  },
  itemLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    width: '100%',
    marginTop: 14,
  },
  successIcon: {
    fontSize: 16,
    color: COLORS.COLOR_GREEN_BASE,

    '&:hover': {
      color: COLORS.COLOR_GREEN_BASE,
    },
  },
  ventureName: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  location: {
    display: '-webkit-box',
    marginTop: 4,
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  channelLink: {
    color: COLORS.COLOR_BLUE_BASE,
    fontSize: 12,
    wordBreak: 'break-word',
  },
  iconsBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 16,

    '& > a': {
      padding: 4,
    },
  },
  actions: {
    marginLeft: 'auto',
  },
}));

function getReportIconColor(disabled?: boolean) {
  return disabled ? COLORS.COLOR_GRAY_BASE : COLORS.COLOR_BLUE_BASE;
}

function getTitle(session: EventType) {
  try {
    return format(new Date(session.start), 'EEE, MMM d, yyyy');
  } catch (error) {
    return '';
  }
}

function getTime(session: EventType) {
  try {
    const startTime = format(new Date(session.start), 'h:mma');
    const endTime = format(new Date(session.end), 'h:mma');
    return `${startTime} - ${endTime}`;
  } catch (error) {
    return '';
  }
}

function SessionCard({
  session,
  cardId,
}: {
  session: EventType;
  cardId: string;
}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { channels, hasAccessToAction } = useContext(UserContext);

  const [isCopied, setIsCopied] = useState(false);

  const resetCopyState = throttle(() => setIsCopied(false), 4000);

  const channel = useMemo(() => {
    if (session.tenantEventChannelId && channels) {
      return channels.find((c) => c.id === session.tenantEventChannelId);
    }
    return null;
  }, [channels, session]);

  const date = getTitle(session);
  const time = getTime(session);
  const disabledAgenda = !session.description || !session.agendaReceived;
  const disabledFounderNotes = !session.founderReportId;
  const disabledLeadReport = !session.leadMentorReportId;

  const handleCopy = (link: string) => {
    navigator.clipboard.writeText(link);
    setIsCopied(true);
    resetCopyState();
  };

  const prevPath = `${location.pathname}${location.search}`;

  const handleCardClick = useCallback(() => {
    history.push({
      pathname: getRoutePath(Pages.SESSIONS_DETAILS, {
        sessionId: session.id,
      }),
      state: { prevPath },
    });
  }, [history, prevPath, session.id]);

  return (
    <div className={classes.card} id={cardId}>
      <Typography
        variant='h4'
        className={classes.date}
        onClick={handleCardClick}>
        <span>{date}</span>
        <span>{time}</span>
      </Typography>
      <Typography
        variant='h5'
        className={classes.cardTitle}
        onClick={handleCardClick}>
        {session.summary}
      </Typography>
      <Text
        variant='normal'
        color={COLORS.COLOR_TEXT_LIGHTENED_20}
        className={classes.ventureName}>
        for {session.ventureName}
      </Text>
      {!!session.physicalLocation && (
        <Text variant='normal' bold className={classes.location}>
          {session.physicalLocation}
        </Text>
      )}
      {channel && (
        <div className={classes.itemLink} data-testid='card-link'>
          <MaterialLink
            className={classes.channelLink}
            href={channel.channelLocation}
            target='_blank'>
            {channel.channelLocation}
          </MaterialLink>
          {!isCopied ? (
            <Tooltip title='Copy link'>
              <span
                data-testid={`copy-channel-link-${session.id}`}
                onClick={() => handleCopy(channel.channelLocation)}>
                <CopyIcon className={classes.icon} />
              </span>
            </Tooltip>
          ) : (
            <CheckCircleIcon
              className={cn(classes.icon, classes.successIcon)}
            />
          )}
        </div>
      )}
      <div className={classes.iconsBlock}>
        <IconButton
          data-testid='attendance-icon'
          component={Link}
          disabled={session.acceptanceStatus === ACCEPTANCE_STATUS.GRAY}
          to={{
            pathname: getRoutePath(Pages.SESSIONS_DETAILS, {
              sessionId: session.id,
            }),
            search: 'anchor=session-attendance-list',
            state: { prevPath },
          }}>
          <AccountBox
            fontSize='large'
            style={{
              color: getAttendanceStatusColor(session.acceptanceStatus),
            }}
          />
        </IconButton>
        <IconButton
          data-testid='agenda-icon'
          component={Link}
          disabled={disabledAgenda}
          to={{
            pathname: getRoutePath(Pages.SESSIONS_DETAILS, {
              sessionId: session.id,
            }),
            search: 'anchor=description',
            state: { prevPath },
          }}>
          <ListAltIcon
            fontSize='large'
            style={{
              color: getReportIconColor(disabledAgenda),
            }}
          />
        </IconButton>
        <IconButton
          data-testid='homework-icon'
          component={Link}
          disabled={!session.homework}
          to={{
            pathname: getRoutePath(Pages.SESSIONS_DETAILS, {
              sessionId: session.id,
            }),
            search: 'anchor=homework',
            state: {
              prevPath: `${location.pathname}${location.search}`,
            },
          }}>
          <AssignmentTurnedInIcon
            fontSize='large'
            style={{
              color: getReportIconColor(!session.homework),
            }}
          />
        </IconButton>
        <IconButton
          data-testid='founder-notes-icon'
          component={Link}
          disabled={disabledFounderNotes}
          to={{
            pathname: getRoutePath(Pages.SESSIONS_NOTES, {
              sessionId: session.id,
            }),
            state: { prevPath },
            search: 'anchor=founder-notes-report-note',
          }}>
          <AssignmentIcon
            fontSize='large'
            style={{
              color: getReportIconColor(disabledFounderNotes),
            }}
          />
        </IconButton>
        {hasAccessToAction('session.details.leadMentorReport') && (
          <IconButton
            data-testid='lead-mentor-report-icon'
            component={Link}
            disabled={disabledLeadReport}
            to={{
              pathname: getRoutePath(Pages.SESSIONS_REPORT, {
                sessionId: session.id,
              }),
              state: { prevPath },
            }}>
            <BallotIcon
              fontSize='large'
              style={{
                color: getReportIconColor(disabledLeadReport),
              }}
            />
          </IconButton>
        )}
        <div className={classes.actions}>
          <SessionsRowActions session={session} variant='list' />
        </div>
      </div>
    </div>
  );
}

export default SessionCard;
